import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import {
    BookOpenIcon,
    CheckCircleIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    ArrowsPointingOutIcon,
    XMarkIcon,
    ListBulletIcon,
    TableCellsIcon,
    ChevronDownIcon,
    DocumentArrowDownIcon,
    DocumentTextIcon,
    LightBulbIcon,
    SparklesIcon,
    ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import { generateSOWPDF } from '../../utils/pdfGenerators';
import { computeLessonDisplayData } from '../../utils/sowLessons';

// Helper to intelligently map existing paragraph text to split curriculum items
const mapItemsToCurriculum = (existingText, curriculumOptions) => {
    if (!existingText || !curriculumOptions || curriculumOptions.length === 0) return existingText;

    // Scaffolding phrases that should NEVER be individual tags
    const SCAFFOLDING_PHRASES = [
        "By the end of the lesson",
        "By the end of the session",
        "The learner should be able to",
        "By the end of this sub-strand",
        "Learner to be able to",
        "In different situations",
        "should be able to",
        "By the end",
        "the learner",
        "be able to",
        "learner should",
        "end of the lesson"
    ];

    // Use a Set for the initial curriculum options to ensure we only work with unique possibilities
    let uniqueOptions = [...new Set(curriculumOptions)];

    // Filter out scaffolding phrases from being candidate tags
    uniqueOptions = uniqueOptions.filter(opt => {
        const normalizedOpt = (opt || '').toLowerCase().trim();
        return !SCAFFOLDING_PHRASES.some(s => normalizedOpt.includes(s.toLowerCase()) && normalizedOpt.length < s.length + 5);
    });

    // Normalize function for fuzzy matching
    const normalize = (val) => {
        const str = Array.isArray(val) ? val.join(' ') : (val || '');
        return str.toLowerCase().replace(/[\s\W_]+/g, '').trim();
    };

    const normalizedText = normalize(existingText);
    if (!normalizedText) return existingText; // Rule: Don't attempt to map empty or whitespace-only strings

    const finalMatches = [];
    for (const opt of uniqueOptions) {
        const normalizedOpt = normalize(opt);
        if (!normalizedOpt || normalizedOpt.length < 3) continue;

        if (normalizedText.includes(normalizedOpt) || normalizedOpt.includes(normalizedText)) {
            finalMatches.push(opt);
        }
    }

    // Keep only the most specific matches (longest strings) and avoid duplicates
    const filteredMatches = finalMatches.filter((match, index) => {
        return !finalMatches.some((other, otherIndex) =>
            otherIndex !== index && other.length > match.length && other.toLowerCase().includes(match.toLowerCase())
        );
    });

    return filteredMatches.length > 0 ? [...new Set(filteredMatches)] : existingText;
};




/**
 * Premium Modal for editing large text blocks in the SOW.
 */
const ContentEditorModal = ({ isOpen, onClose, onSave, initialValue, title, icon: Icon }) => {
    const [tempValue, setTempValue] = useState(initialValue || '');

    useEffect(() => {
        setTempValue(initialValue || '');
    }, [initialValue, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-8 duration-500">
                {/* Header */}
                <div className="px-8 py-6 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center shadow-sm border border-indigo-100/50">
                            {Icon ? <Icon className="w-6 h-6 text-[#18216D]" /> : <PencilIcon className="w-6 h-6 text-[#18216D]" />}
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-0.5">Advanced Editor</p>
                            <h3 className="text-lg font-black text-[#18216D] tracking-tight">{title}</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-all text-slate-400">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8">
                    <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl border border-amber-100/50">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <p className="text-[10px] font-bold text-amber-700 uppercase tracking-tight">Tip: Use new lines to separate different items or questions.</p>
                    </div>

                    <textarea
                        autoFocus
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="w-full h-64 bg-slate-50/30 border border-slate-100 rounded-2xl p-6 text-sm font-bold text-slate-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-200 transition-all resize-none custom-scrollbar"
                        placeholder="Type or paste your content here..."
                    />
                </div>

                {/* Footer */}
                <div className="px-8 py-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSave(tempValue)}
                        className="px-8 py-3 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-[0.15em] shadow-lg shadow-indigo-200 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
                    >
                        <CheckCircleIcon className="w-4 h-4" />
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Minimalistic Tooltip — white bg, navy text, small caret
// ---------------------------------------------------------------------------
const SOWTooltip = ({ text, position = 'top', children }) => {
    const [visible, setVisible] = useState(false);
    return (
        <div
            className="relative inline-flex"
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
        >
            {children}
            {visible && (
                <div
                    className={`pointer-events-none absolute z-[300] whitespace-nowrap ${position === 'top'
                        ? 'bottom-full left-1/2 -translate-x-1/2 mb-2'
                        : 'top-full left-1/2 -translate-x-1/2 mt-2'
                        }`}
                >
                    <div className="px-3 py-1.5 bg-white text-[#18216D] text-[9px] font-black uppercase tracking-[0.15em] rounded-lg shadow-lg border border-slate-100 animate-in fade-in duration-150">
                        {text}
                    </div>
                    {/* caret */}
                    <div
                        className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 ${position === 'top'
                            ? 'top-full border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-white'
                            : 'bottom-full border-l-[5px] border-r-[5px] border-b-[5px] border-l-transparent border-r-transparent border-b-white'
                            }`}
                    />
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Full-width Row Editor Panel — drops below the selected row
// ---------------------------------------------------------------------------
const RowEditorPanel = ({
    lesson, lessonIndex, weekNum,
    strandOptions, subStrandOptions, outcomeOptions, experienceOptions,
    registrySubStrand, curriculumTree,
    handleUpdateLesson, setRowEditingId, handleSaveRoadmap, setEditorModal,
    teacherMode, showToast,
    isTable = true,
}) => {
    const { post } = useApi();
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [suggestionsApplied, setSuggestionsApplied] = useState(false);

    const handleAISuggest = async () => {
        const subStrandId = registrySubStrand?.id;
        if (!subStrandId) {
            if (showToast) showToast('Please select a valid Sub-Strand from the dropdown first to get AI suggestions.', 'warning');
            return;
        }

        setIsSuggesting(true);
        try {
            const response = await post('/api/cbc/schemes-of-work/autocomplete-row/', {
                sub_strand_id: subStrandId
            });

            if (response?.success && response?.suggestions) {
                const { specific_outcomes, learning_experiences, key_inquiry_questions, resources, assessment } = response.suggestions;

                // 1. Learning Outcomes: Merge directly into primary specific_outcomes dropdown selection
                if (specific_outcomes && specific_outcomes.length > 0) {
                    const currentOutcomes = Array.isArray(lesson.specific_outcomes) 
                        ? lesson.specific_outcomes 
                        : (lesson.specific_outcomes ? [lesson.specific_outcomes] : []);
                    const mergedOutcomes = [...new Set([...currentOutcomes, ...specific_outcomes])];
                    handleUpdateLesson(lesson.$$id, 'specific_outcomes', mergedOutcomes);
                    handleUpdateLesson(lesson.$$id, 'imported_outcomes', []);
                }

                // 2. Learning Experiences: Merge directly into primary learning_experiences dropdown selection
                if (learning_experiences && learning_experiences.length > 0) {
                    const currentExp = Array.isArray(lesson.learning_experiences) 
                        ? lesson.learning_experiences 
                        : (lesson.learning_experiences ? [lesson.learning_experiences] : []);
                    const mergedExp = [...new Set([...currentExp, ...learning_experiences])];
                    handleUpdateLesson(lesson.$$id, 'learning_experiences', mergedExp);
                    handleUpdateLesson(lesson.$$id, 'imported_experiences', []);
                }

                // 3. Inquiry Questions: Populate directly into the multiline text input box
                if (key_inquiry_questions && key_inquiry_questions.length > 0) {
                    const questionsText = Array.isArray(key_inquiry_questions) 
                        ? key_inquiry_questions.join('\n') 
                        : String(key_inquiry_questions);
                    const currentQText = typeof lesson.key_inquiry_questions === 'string' 
                        ? lesson.key_inquiry_questions 
                        : (Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions.join('\n') : '');
                    const finalQText = currentQText.trim() ? `${currentQText.trim()}\n${questionsText}` : questionsText;
                    handleUpdateLesson(lesson.$$id, 'key_inquiry_questions', finalQText);
                    handleUpdateLesson(lesson.$$id, 'imported_inquiry_questions', []);
                }

                // 4. Resources & Assessment
                if (resources) {
                    handleUpdateLesson(lesson.$$id, 'resources', resources);
                }
                if (assessment) {
                    handleUpdateLesson(lesson.$$id, 'assessment', assessment);
                }

                setSuggestionsApplied(true);
                if (showToast) showToast('AI suggestions applied directly to lesson fields!', 'success');
            } else {
                if (showToast) showToast(response?.error || 'Failed to fetch AI suggestions.', 'error');
            }
        } catch (err) {
            if (showToast) showToast(err.message || 'AI suggest call failed.', 'error');
        } finally {
            setIsSuggesting(false);
        }
    };

    // Helper: toggle a value in a multi-select array stored on the lesson
    const toggle = (field, val) => {
        const current = Array.isArray(lesson[field]) ? lesson[field] : (lesson[field] ? [lesson[field]] : []);
        const updated = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
        handleUpdateLesson(lesson.$$id, field, updated);
    };

    const remove = (field, val) => {
        const current = Array.isArray(lesson[field]) ? lesson[field] : (lesson[field] ? [lesson[field]] : []);
        handleUpdateLesson(lesson.$$id, field, current.filter(v => v !== val));
    };

    const vals = (field) => Array.isArray(lesson[field]) ? lesson[field] : (lesson[field] ? [lesson[field]] : []);

    const fieldLessonTitle = (
        <div>
            <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1.5 block">Lesson Title</span>
            <input
                type="text"
                value={lesson.lesson_title || lesson.title || ''}
                onChange={(e) => {
                    handleUpdateLesson(lesson.$$id, 'lesson_title', e.target.value);
                    handleUpdateLesson(lesson.$$id, 'title', e.target.value);
                }}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all placeholder:text-slate-300 placeholder:font-normal"
                placeholder="e.g. Introduction to Fractions, Plant Cell Structure..."
            />
        </div>
    );

    const fieldStrand = (
        <div>
            <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1.5 block">Strand</span>
            <SearchableDropdown
                options={strandOptions.map(s => ({ id: s, name: s }))}
                selectedValues={vals('strand')}
                onChange={(val) => toggle('strand', val)}
                placeholder="Select Strand"
                isMultiple={true}
                compact={true}
                displayKey="name"
            />
            {vals('strand').length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {vals('strand').map(v => (
                        <span key={v} className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-[#18216D] border border-indigo-100 rounded-lg text-[9px] font-black uppercase tracking-tight">
                            {v}
                            <button onClick={() => remove('strand', v)} className="ml-0.5 text-indigo-300 hover:text-indigo-600 transition-colors"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );

    const fieldSubStrand = (
        <div>
            <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1.5 block">Sub-Strand</span>
            <SearchableDropdown
                options={subStrandOptions.map(s => ({ id: s, name: s }))}
                selectedValues={vals('sub_strand')}
                onChange={(val) => {
                    toggle('sub_strand', val);
                    const existingInquiry = vals('key_inquiry_questions');
                    if (!existingInquiry || existingInquiry.length === 0 || (typeof lesson.key_inquiry_questions === 'string' && !lesson.key_inquiry_questions.trim())) {
                        const matchedSS = (curriculumTree?.strands || [])
                            .flatMap(s => s.sub_strands || [])
                            .find(ss => ss.name === val || String(ss.id) === String(val)) || registrySubStrand;
                        const questions = [
                            ...(matchedSS?.registry_questions?.map(q => q.question) || []),
                            ...(Array.isArray(matchedSS?.key_inquiry_questions) ? matchedSS.key_inquiry_questions : (matchedSS?.key_inquiry_questions ? [matchedSS.key_inquiry_questions] : []))
                        ].filter(Boolean);
                        if (questions.length > 0) {
                            handleUpdateLesson(lesson.$$id, 'key_inquiry_questions', questions.join('\n'));
                        }
                    }
                }}
                placeholder="Select Sub-Strand"
                isMultiple={true}
                compact={true}
                displayKey="name"
            />
            {vals('sub_strand').length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {vals('sub_strand').map(v => (
                        <span key={v} className="flex items-center gap-1 px-2 py-1 bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-[9px] font-black">
                            {v}
                            <button onClick={() => remove('sub_strand', v)} className="ml-0.5 text-slate-300 hover:text-rose-500 transition-colors"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );

    const fieldOutcomes = (
        <div>
            <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1.5 block">Learning Outcomes</span>
            <SearchableDropdown
                options={outcomeOptions.map(o => ({ id: o, name: o }))}
                selectedValues={[...vals('specific_outcomes'), ...vals('imported_outcomes')]}
                onChange={(val) => {
                    if (vals('imported_outcomes').includes(val)) {
                        remove('imported_outcomes', val);
                    } else if (vals('specific_outcomes').includes(val)) {
                        remove('specific_outcomes', val);
                    } else {
                        // When adding a new one, add to specific_outcomes
                        toggle('specific_outcomes', val);
                    }
                }}
                placeholder="Select Outcomes"
                isMultiple={true}
                compact={true}
                displayKey="name"
            />
            {vals('specific_outcomes').length > 0 && (
                <div className="flex flex-col gap-1 mt-2">
                    {vals('specific_outcomes').map(v => (
                        <span key={v} className="flex items-start gap-1 px-2 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-lg text-[9px] font-bold leading-snug">
                            <CheckCircleIcon className="w-3 h-3 flex-shrink-0 mt-0.5 text-emerald-500" />
                            <span className="flex-1">{v}</span>
                            <button onClick={() => remove('specific_outcomes', v)} className="ml-1 text-emerald-300 hover:text-rose-500 flex-shrink-0 transition-colors"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
            {vals('imported_outcomes').length > 0 && (
                <div className="flex flex-col gap-1 mt-2 border-t border-slate-100 pt-2">
                    <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1 block">Imported Outcomes</span>
                    {vals('imported_outcomes').map(v => (
                        <span key={v} className="flex items-start gap-1 px-2 py-1.5 bg-slate-50 text-slate-600 border border-slate-100 rounded-lg text-[9px] font-bold leading-snug group">
                            <DocumentTextIcon className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400" />
                            <span className="flex-1">{v}</span>
                            <button onClick={() => remove('imported_outcomes', v)} className="ml-1 text-slate-300 hover:text-rose-500 flex-shrink-0 transition-colors opacity-0 group-hover:opacity-100"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );

    const fieldExperiences = (
        <div>
            <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1.5 block">Learning Experiences</span>
            <SearchableDropdown
                options={experienceOptions.map(o => ({ id: o, name: o }))}
                selectedValues={[...vals('learning_experiences'), ...vals('imported_experiences')]}
                onChange={(val) => {
                    if (vals('imported_experiences').includes(val)) {
                        remove('imported_experiences', val);
                    } else if (vals('learning_experiences').includes(val)) {
                        remove('learning_experiences', val);
                    } else {
                        toggle('learning_experiences', val);
                    }
                }}
                placeholder="Select Experiences"
                isMultiple={true}
                compact={true}
                displayKey="name"
            />
            {vals('learning_experiences').length > 0 && (
                <div className="flex flex-col gap-1 mt-2">
                    {vals('learning_experiences').map(v => (
                        <span key={v} className="flex items-start gap-1 px-2 py-1.5 bg-blue-50 text-blue-800 border border-blue-100 rounded-lg text-[9px] font-bold leading-snug">
                            <BookOpenIcon className="w-3 h-3 flex-shrink-0 mt-0.5 text-blue-400" />
                            <span className="flex-1">{v}</span>
                            <button onClick={() => remove('learning_experiences', v)} className="ml-1 text-blue-200 hover:text-rose-500 flex-shrink-0 transition-colors"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
            {vals('imported_experiences').length > 0 && (
                <div className="flex flex-col gap-1 mt-2 border-t border-slate-100 pt-2">
                    <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1 block">Imported Experiences</span>
                    {vals('imported_experiences').map(v => (
                        <span key={v} className="flex items-start gap-1 px-2 py-1.5 bg-teal-50 text-teal-700 border border-teal-100 rounded-lg text-[9px] font-bold leading-snug group">
                            <LightBulbIcon className="w-3 h-3 flex-shrink-0 mt-0.5 text-teal-500" />
                            <span className="flex-1">{v}</span>
                            <button onClick={() => remove('imported_experiences', v)} className="ml-1 text-teal-300 hover:text-rose-500 flex-shrink-0 transition-colors opacity-0 group-hover:opacity-100"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );



    const fieldResources = (
        <div>
            <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1.5 block">Resources & Materials</span>
            <textarea
                rows={3}
                value={lesson.resources || ''}
                onChange={(e) => handleUpdateLesson(lesson.$$id, 'resources', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 resize-none transition-all"
                placeholder="e.g. Textbook pp. 24-31, charts, digital devices..."
            />
        </div>
    );

    const currentInquiryText = typeof lesson.key_inquiry_questions === 'string'
        ? lesson.key_inquiry_questions
        : (Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions.join('\n') : '');

    const handleAutoPopulateInquiry = () => {
        const questions = [
            ...(registrySubStrand?.registry_questions?.map(q => q.question) || []),
            ...(Array.isArray(registrySubStrand?.key_inquiry_questions) ? registrySubStrand.key_inquiry_questions : (registrySubStrand?.key_inquiry_questions ? [registrySubStrand.key_inquiry_questions] : []))
        ].filter(Boolean);

        if (questions.length > 0) {
            handleUpdateLesson(lesson.$$id, 'key_inquiry_questions', questions.join('\n'));
            if (showToast) showToast('Autopopulated inquiry questions from curriculum!', 'success');
        } else {
            if (showToast) showToast('No inquiry questions found in registry for this sub-strand.', 'info');
        }
    };

    const fieldInquiry = (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em]">Inquiry Questions</span>
                <button
                    type="button"
                    onClick={handleAutoPopulateInquiry}
                    className="text-[8px] font-black text-amber-600 hover:text-amber-800 uppercase tracking-wider underline cursor-pointer"
                >
                    Autopopulate
                </button>
            </div>
            <textarea
                rows={3}
                value={currentInquiryText}
                onChange={(e) => handleUpdateLesson(lesson.$$id, 'key_inquiry_questions', e.target.value)}
                placeholder="Type or edit inquiry questions (one per line)..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-300 resize-none min-h-[70px] transition-all"
            />
            {vals('imported_inquiry_questions').length > 0 && (
                <div className="flex flex-col gap-1 mt-2 border-t border-slate-100 pt-2">
                    <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] mb-1 block">Imported Questions</span>
                    {vals('imported_inquiry_questions').map(v => (
                        <span key={v} className="flex items-start gap-1 px-2 py-1.5 bg-slate-50 text-slate-600 border border-slate-100 rounded-lg text-[9px] font-bold leading-snug group">
                            <span className="text-amber-400 font-black flex-shrink-0 mt-0.5">?</span>
                            <span className="flex-1">{v}</span>
                            <button onClick={() => remove('imported_inquiry_questions', v)} className="ml-1 text-slate-300 hover:text-rose-500 flex-shrink-0 transition-colors opacity-0 group-hover:opacity-100"><XMarkIcon className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );

    const fieldAssessment = (
        <div>
            <div className="flex items-center mb-1.5">
                <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em]">Assessment Methods</span>
            </div>
            <textarea
                rows={3}
                value={typeof lesson.assessment === 'string' ? lesson.assessment : (Array.isArray(lesson.assessment) ? lesson.assessment.join('\n') : '')}
                onChange={(e) => handleUpdateLesson(lesson.$$id, 'assessment', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-300 resize-none transition-all"
                placeholder="e.g. Observation, Portfolio, Written test..."
            />
        </div>
    );

    const content = (
        <div className={`${isTable ? 'mx-3 mb-4' : 'w-full mb-0'} bg-white border-2 border-indigo-200/60 rounded-2xl shadow-xl overflow-visible`}>
            {/* Header strip */}
            <div className="flex items-center justify-between px-6 py-3.5 bg-indigo-50/60 border-b border-indigo-100">
                <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                    <span className="text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] flex items-center gap-1.5">
                        Editing — Week {weekNum} · Lesson(s):
                        <input 
                            type="text" 
                            className="w-20 px-2 py-0.5 bg-white border border-indigo-200 rounded text-center focus:outline-none focus:ring-1 focus:ring-indigo-400 placeholder:text-indigo-200/60"
                            value={lesson.lessons || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                const parts = val.split(/[- ,]+/).filter(Boolean);
                                if (parts.length > 2) {
                                    if (showToast) showToast('You cannot bundle more than 2 lessons in a single card.', 'warning');
                                    return;
                                }
                                handleUpdateLesson(lesson.$$id, 'lessons', val);
                            }}
                            placeholder={lessonIndex + 1}
                        />
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {vals('sub_strand').length > 0 && !suggestionsApplied && (
                        <button
                            onClick={handleAISuggest}
                            disabled={isSuggesting}
                            type="button"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-[9px] font-black uppercase tracking-widest transition-all disabled:opacity-50 shadow-sm"
                        >
                            {isSuggesting ? (
                                <span className="animate-spin text-indigo-600">🌀</span>
                            ) : (
                                <SparklesIcon className="w-3.5 h-3.5 text-indigo-600" />
                            )}
                            {isSuggesting ? 'Suggesting...' : 'AI Suggest'}
                        </button>
                    )}
                    {suggestionsApplied && (
                        <button
                            onClick={() => setSuggestionsApplied(false)}
                            type="button"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-black uppercase tracking-widest shadow-sm"
                        >
                            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                            Accept
                        </button>
                    )}
                    <button
                        onClick={() => setRowEditingId(null)}
                        className="px-4 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-rose-500 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => { setRowEditingId(null); handleSaveRoadmap(); }}
                        className="px-5 py-2 bg-[#18216D] text-white rounded-xl text-[9px] font-black uppercase tracking-[0.15em] shadow-md hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 border-b-2 border-[#FFC425]"
                    >
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        Save Row
                    </button>
                </div>
            </div>

            <div className="p-5 space-y-4">
                {!isTable ? (
                    <>
                        {/* Row 0: Lesson Title */}
                        <div className="grid grid-cols-1 gap-4">
                            {fieldLessonTitle}
                        </div>
                        {/* Row 1: Strand & Sub-strand */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fieldStrand}
                            {fieldSubStrand}
                        </div>
                        {/* Row 2: Outcomes & Experiences */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fieldOutcomes}
                            {fieldExperiences}
                        </div>
                        {/* Row 3: Inquiry Questions */}
                        <div className="grid grid-cols-1 gap-4">
                            {fieldInquiry}
                        </div>
                        {/* Row 4: Resources & Assessment */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fieldResources}
                            {fieldAssessment}
                        </div>
                    </>
                ) : (
                    <>
                        {/* Row 0: Lesson Title */}
                        <div className="grid grid-cols-1 gap-4">
                            {fieldLessonTitle}
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-slate-100" />

                        {/* Row 1: Curriculum Cascade */}
                        <div className="grid grid-cols-3 gap-4">
                            {fieldStrand}
                            {fieldSubStrand}
                            {fieldOutcomes}
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-slate-100" />

                        {/* Row 2: Experiences & Resources */}
                        <div className="grid grid-cols-2 gap-4">
                            {fieldExperiences}
                            {fieldResources}
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-slate-100" />

                        {/* Row 3: Enquiry & Assessment */}
                        <div className="grid grid-cols-2 gap-4">
                            {fieldInquiry}
                            {fieldAssessment}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    if (!isTable) return content;

    return (
        <tr className="animate-in fade-in slide-in-from-top-2 duration-300">
            <td colSpan={7} className="p-0 border-t-0">
                {content}
            </td>
        </tr>
    );
};

const sowGlobalCache = {
    learningAreas: null,
    academicTerms: null,
    teacherSow: {},
    curriculumData: {}
};

const SchemeOfWorkManagement = ({
    teacherMode = false,
    learningAreaId = null,
    sowId = null,
    teacherId = null,
    teacherName = null,
    readOnly = false,
    autoOpenRoadmap = false,
    isFullScreen = false,
    isHOS = false,
    onBack,
}) => {
    const { get, post, put } = useApi();
    const { showToast } = useAppState();
    const location = useLocation();
    const navigate = useNavigate();
    const initialSearchRef = useRef(location.search);

    // const [schemes, setSchemes] = useState([]);
    // const [learningAreas, setLearningAreas] = useState([]);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    // const [showModal, setShowModal] = useState(false);
    // const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedScheme, setSelectedScheme] = useState(null);
    // const [formData, setFormData] = useState({
    //     learning_area: '',
    //     academic_term: '',
    //     title: '',
    //     description: '',
    //     weekly_breakdown: []
    // });
    const [submitting, setSubmitting] = useState(false);
    // const [error, setError] = useState(null);
    const [collapsedWeeks, setCollapsedWeeks] = useState(new Set());

    const toggleWeek = (weekNum) => {
        setCollapsedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(weekNum)) next.delete(weekNum);
            else next.add(weekNum);
            return next;
        });
    };
    const [filterTerm, setFilterTerm] = useState('');
    // const [filterArea, setFilterArea] = useState('');
    const [viewMode, setViewMode] = useState(isFullScreen ? 'table' : 'list');

    useEffect(() => {
        if (isFullScreen) {
            setViewMode('table');
        }
    }, [isFullScreen]);
    const [isAutoView, setIsAutoView] = useState(true);
    const [isEditingRoadmap, setIsEditingRoadmap] = useState(false);
    const [rowEditingId, setRowEditingId] = useState(null);
    const [roadmapEdits, setRoadmapEdits] = useState([]);
    const [lessonToDelete, setLessonToDelete] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    // const [showSOWDeleteModal, setShowSOWDeleteModal] = useState(false);
    // const [sOWToDelete, setSOWToDelete] = useState(null);
    const [curriculumTree, setCurriculumTree] = useState(null);
    const [lastAddedRowId, setLastAddedRowId] = useState(null);
    const [generatingLessonId, setGeneratingLessonId] = useState(null);
    const [editorModal, setEditorModal] = useState({
        isOpen: false,
        field: '',
        lessonId: null,
        content: '',
        title: ''
    });
    const tableBodyRef = useRef(null);

    const [showImportModal, setShowImportModal] = useState(false);
    const [showActionModal, setShowActionModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importStep, setImportStep] = useState(0);
    const [importError, setImportError] = useState(null);

    const handleExecuteImport = async () => {
        if (!importFile) return;
        setImporting(true);
        setImportError(null);
        setImportStep(1);

        const stepTimer1 = setTimeout(() => setImportStep(2), 2500);
        const stepTimer2 = setTimeout(() => setImportStep(3), 8000);
        const stepTimer3 = setTimeout(() => setImportStep(4), 14000);

        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('learning_area', learningAreaId);
            formData.append('academic_term', filterTerm || terms.find(t => t.is_current)?.id || terms[0]?.id);

            const response = await post('/api/cbc/schemes-of-work/import-sow/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response?.success && response?.data) {
                showToast(response.message || 'Scheme of Work imported successfully!', 'success');
                setShowImportModal(false);
                setImportFile(null);
                // Invalidate cache so fetchData re-fetches from backend
                const cacheKey = `${learningAreaId}`;
                delete sowGlobalCache.teacherSow[cacheKey];
                // Apply imported data immediately to avoid requiring a page refresh
                if (curriculumTree && response.data) {
                    applyDetailToRoadmap(response.data, curriculumTree);
                } else {
                    if (response.data.weekly_breakdown) {
                        setRoadmapEdits(response.data.weekly_breakdown);
                    }
                    if (fetchData) await fetchData();
                }
            } else {
                setImportError(response?.error || 'Failed to import Scheme of Work.');
            }
        } catch (err) {
            console.error('SOW Import Error:', err);
            const serverError = err.response?.data?.error || err.data?.error || err.message || 'Import failed. Please check your document and try again.';
            setImportError(serverError);
        } finally {
            clearTimeout(stepTimer1);
            clearTimeout(stepTimer2);
            clearTimeout(stepTimer3);
            setImporting(false);
            setImportStep(0);
        }
    };

    // Click outside listener for row editing removed as requested so editing doesn't close unexpectedly
    useEffect(() => {
        const handleClickOutside = (event) => {
            // No longer resetting rowEditingId here so it remains open until cancel is clicked
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [rowEditingId]);

    // Auto-resize textareas function
    const autoResize = (e) => {
        const target = e.target;
        target.style.height = 'auto';
        target.style.height = `${target.scrollHeight}px`;
    };

    // Trigger auto-resize on all textareas in the editing row
    useEffect(() => {
        if (rowEditingId) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                const textareas = tableBodyRef.current?.querySelectorAll('textarea');
                textareas?.forEach(ta => {
                    ta.style.height = 'auto';
                    ta.style.height = `${ta.scrollHeight}px`;
                });
            }, 50);
        }
    }, [rowEditingId]);




    const applyDetailToRoadmap = (detailData, curriculumData) => {
        const sOptions = [...new Set(curriculumData?.strands?.map(s => s.name) || [])];
        const ssOptions = [...new Set(curriculumData?.strands?.flatMap(s => s.sub_strands || [])?.map(ss => ss.name) || [])];
        const loOptions = [...new Set(curriculumData?.strands?.flatMap(s => s.sub_strands || [])?.flatMap(ss => ss.learning_outcomes || [])?.map(lo => lo.description) || [])];

        const mappedEdits = (detailData.weekly_breakdown || []).map(w => ({
            ...w,
            week_number: w.week_number || w.week || 1,
            $$id: w.$$id || Math.random().toString(36).substr(2, 9),
            strand: mapItemsToCurriculum(w.strand, sOptions),
            sub_strand: mapItemsToCurriculum(w.sub_strand, ssOptions),
            specific_outcomes: mapItemsToCurriculum(w.specific_outcomes, loOptions),
            learning_experiences: w.learning_experiences
        }));

        setSelectedScheme(detailData);
        setCurriculumTree(curriculumData);
        setRoadmapEdits(mappedEdits);
        setIsAutoView(true);
    };

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            // setError(null);

            if (isHOS && sowId) {
                let termsData = sowGlobalCache.academicTerms;
                if (!termsData) {
                    termsData = await get('/api/academic-terms/');
                    sowGlobalCache.academicTerms = termsData;
                }
                setTerms(termsData);

                const detailData = await get(`/api/cbc/schemes-of-work/${sowId}/`);
                const resolvedAreaId = learningAreaId || detailData.learning_area?.id || detailData.learning_area;

                let curriculumData = resolvedAreaId ? sowGlobalCache.curriculumData[resolvedAreaId] : null;
                if (!curriculumData && resolvedAreaId) {
                    curriculumData = await get(`/api/cbc/learning-areas/${resolvedAreaId}/`);
                    sowGlobalCache.curriculumData[resolvedAreaId] = curriculumData;
                }

                applyDetailToRoadmap(detailData, curriculumData);
            } else if (teacherMode && learningAreaId) {
                // Teacher mode: fetch learning areas and terms first
                let areasData = sowGlobalCache.learningAreas;
                let termsData = sowGlobalCache.academicTerms;

                if (!areasData || !termsData) {
                    const [areasRes, termsRes] = await Promise.all([
                        get('/api/cbc/learning-areas/'),
                        get('/api/academic-terms/')
                    ]);
                    areasData = areasRes;
                    termsData = termsRes;
                    sowGlobalCache.learningAreas = areasData;
                    sowGlobalCache.academicTerms = termsData;
                }

                // setLearningAreas(areasData);
                setTerms(termsData);

                // Find current term
                const currentTerm = termsData.find(t => t.is_current);

                let termIdQuery = currentTerm ? `&academic_term=${currentTerm.id}` : '';

                const cacheKey = `${learningAreaId}`;
                let detailData = sowGlobalCache.teacherSow[cacheKey];

                if (!detailData) {
                    try {
                        // Fetch the teacher's isolated SOW (or master fallback, handled cleanly by backend)
                        const sowResponse = await get(`/api/cbc/schemes-of-work/teacher-sow/?learning_area=${learningAreaId}${termIdQuery}`);
                        detailData = sowResponse.data;
                        sowGlobalCache.teacherSow[cacheKey] = detailData;
                    } catch (e) {
                        console.error("No SOW found for teacher view.", e);
                    }
                }

                if (detailData) {
                    // Try to use the resolved learning area ID from the detailData, fallback to learningAreaId
                    const resolvedAreaId = learningAreaId || detailData.learning_area?.id || detailData.learning_area;

                    let curriculumData = sowGlobalCache.curriculumData[resolvedAreaId];
                    if (!curriculumData) {
                        curriculumData = await get(`/api/cbc/learning-areas/${resolvedAreaId}/`);
                        sowGlobalCache.curriculumData[resolvedAreaId] = curriculumData;
                    }

                    applyDetailToRoadmap(detailData, curriculumData);
                }
            } else {
                // Admin mode: fetch all schemes
                const [schemesData, termsData] = await Promise.all([
                    get('/api/cbc/schemes-of-work/'),
                    get('/api/academic-terms/')
                ]);

                // setSchemes(schemesData);
                // setLearningAreas(areasData);
                setTerms(termsData);

                // Handle deep linking from Curriculum Registry
                const queryParams = new URLSearchParams(initialSearchRef.current);
                const areaId = queryParams.get('area');
                if (areaId) {
                    // setFilterArea(areaId);
                    // Find matching scheme (prefer active/current term)
                    const currentTerm = termsData.find(t => t.is_current);
                    const matchingScheme = schemesData.find(s =>
                        s.learning_area === parseInt(areaId) &&
                        (!currentTerm || s.academic_term === currentTerm.id)
                    ) || schemesData.find(s => s.learning_area === parseInt(areaId));

                    if (matchingScheme) {
                        const [detailData, curriculumData] = await Promise.all([
                            get(`/api/cbc/schemes-of-work/${matchingScheme.id}/`),
                            get(`/api/cbc/learning-areas/${areaId}/`)
                        ]);

                        // Optimization: Prepare curriculum options lists ONCE outside the loop
                        const sOptions = [...new Set(curriculumData?.strands?.map(s => s.name) || [])];
                        const ssOptions = [...new Set(curriculumData?.strands?.flatMap(s => s.sub_strands || [])?.map(ss => ss.name) || [])];
                        const loOptions = [...new Set(curriculumData?.strands?.flatMap(s => s.sub_strands || [])?.flatMap(ss => ss.learning_outcomes || [])?.map(lo => lo.description) || [])];

                        // Map existing data to curriculum items
                        const mappedEdits = (detailData.weekly_breakdown || []).map(w => ({
                            ...w,
                            week_number: w.week_number || w.week || 1,
                            $$id: Math.random().toString(36).substr(2, 9),
                            strand: mapItemsToCurriculum(w.strand, sOptions),
                            sub_strand: mapItemsToCurriculum(w.sub_strand, ssOptions),
                            specific_outcomes: mapItemsToCurriculum(w.specific_outcomes, loOptions),
                            learning_experiences: w.learning_experiences
                        }));

                        setSelectedScheme(detailData);
                        setCurriculumTree(curriculumData);
                        setRoadmapEdits(mappedEdits);
                        setIsAutoView(true);
                    }
                } else {
                    // Default to current term if available (only if not deep linking to an area)
                    const currentTerm = termsData.find(t => t.is_current);
                    if (currentTerm && !filterTerm) {
                        setFilterTerm(currentTerm.id.toString());
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            // setError('Failed to load schemes. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [get, teacherMode, learningAreaId, sowId, isHOS, filterTerm]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /* Unused functions removed for linting:
       handleCreate, handleEdit, handleView, handleDelete, confirmDeleteSOW,
       handleSubmit, addWeek, updateWeek, removeWeek
    */

    const handleExportPDF = async (scheme) => {
        try {
            await generateSOWPDF(scheme, roadmapEdits);
        } catch (err) {
            console.error("PDF Export Error: ", err);
        }
    };

    const handleQuickAddWeek = async (scheme) => {
        const nextWeek = (roadmapEdits?.length > 0 ? Math.max(...roadmapEdits.map(w => w.week_number || 0)) : 0) + 1;
        const newWeek = {
            $$id: Math.random().toString(36).substr(2, 9),
            week_number: nextWeek,
            lesson_title: '',
            strand: '',
            sub_strand: 'New Lesson Content',
            specific_outcomes: '',
            key_inquiry_questions: '',
            learning_experiences: '',
            resources: ''
        };

        setLastAddedRowId(newWeek.$$id);
        setRoadmapEdits(prev => [...prev, newWeek]);
        setRowEditingId(newWeek.$$id); // Edit only this new row

        // Reset animation after delay
        setTimeout(() => setLastAddedRowId(null), 1000);
    };

    const handleAddLessonToWeek = (weekNum, afterId = null) => {
        const newLesson = {
            $$id: Math.random().toString(36).substr(2, 9),
            week_number: weekNum,
            lesson_title: '',
            strand: '',
            sub_strand: 'New Lesson Topic',
            specific_outcomes: '',
            key_inquiry_questions: '',
            learning_experiences: '',
            resources: ''
        };

        setRoadmapEdits(prev => {
            if (afterId) {
                const index = prev.findIndex(l => l.$$id === afterId);
                if (index !== -1) {
                    const next = [...prev];
                    next.splice(index + 1, 0, newLesson);
                    return next;
                }
            }
            return [...prev, newLesson];
        });

        setLastAddedRowId(newLesson.$$id);
        setRowEditingId(newLesson.$$id);
        // Do NOT set isEditingRoadmap(true) here, it would activate editing across the entire table
        // setIsEditingRoadmap(true);

        // Reset the animation ID after a delay
        setTimeout(() => setLastAddedRowId(null), 1000);
    };

    const handleAddWeekBelow = (afterWeekNum) => {
        const newWeekNum = parseInt(afterWeekNum) + 1;
        const newLesson = {
            $$id: Math.random().toString(36).substr(2, 9),
            week_number: newWeekNum,
            lesson_title: '',
            strand: '',
            sub_strand: 'New Lesson Content',
            specific_outcomes: '',
            key_inquiry_questions: '',
            learning_experiences: '',
            resources: ''
        };

        setRoadmapEdits(prev => {
            // Shift all weeks that are >= newWeekNum up by 1
            const next = prev.map(row => {
                const rowWeek = row.week_number || row.week || 1;
                if (rowWeek >= newWeekNum) {
                    return { ...row, week_number: rowWeek + 1, week: rowWeek + 1 };
                }
                return row;
            });
            next.push(newLesson);
            return next;
        });

        setLastAddedRowId(newLesson.$$id);
        setRowEditingId(newLesson.$$id);
        setTimeout(() => setLastAddedRowId(null), 1000);
    };

    const handleGenerateLessonPlanFromSOW = async (lesson, weekNum) => {
        if (!lesson.sub_strand) {
            showToast('Please specify a sub-strand for this lesson first.', 'warning');
            return;
        }

        const lessonKey = lesson.$$id || lesson.id || `${weekNum}-${lesson.sub_strand}`;
        setGeneratingLessonId(lessonKey);

        // Immediate feedback with 15-second duration so toast stays visible during AI generation
        showToast(`Generating lesson plan for Week ${weekNum}... This may take a moment.`, 'info', { duration: 15000 });

        // Comprehensive Frontend Validation
        const getArrayOrStringLength = (val) => Array.isArray(val) ? val.length : (val ? String(val).trim().length : 0);

        if (!getArrayOrStringLength(lesson.specific_outcomes) && !getArrayOrStringLength(lesson.imported_outcomes)) {
            showToast('Missing learning outcomes. Please specify specific outcomes for this lesson.', 'warning');
            setGeneratingLessonId(null);
            return;
        }
        if (!getArrayOrStringLength(lesson.learning_experiences) && !getArrayOrStringLength(lesson.activities) && !getArrayOrStringLength(lesson.imported_experiences)) {
            showToast('Missing learning experiences. Please specify activities for this lesson.', 'warning');
            setGeneratingLessonId(null);
            return;
        }
        if (!getArrayOrStringLength(lesson.key_inquiry_questions) && !getArrayOrStringLength(lesson.inquiry_questions) && !getArrayOrStringLength(lesson.imported_inquiry_questions)) {
            showToast('Missing key inquiry questions for this lesson.', 'warning');
            setGeneratingLessonId(null);
            return;
        }
        if (!getArrayOrStringLength(lesson.resources) && !getArrayOrStringLength(lesson.learning_resources)) {
            showToast('Missing learning resources for this lesson.', 'warning');
            setGeneratingLessonId(null);
            return;
        }
        if (!getArrayOrStringLength(lesson.assessment) && !getArrayOrStringLength(lesson.assessment_methods)) {
            showToast('Missing assessment methods for this lesson.', 'warning');
            setGeneratingLessonId(null);
            return;
        }

        try {
            setSubmitting(true);

            // We need the actual sub-strand ID. The frontend 'lesson.sub_strand' might be a string if custom
            // or an array. Let's try to find the matching SubStrand ID from the curriculumTree
            let subStrandId = null;
            if (curriculumTree?.strands) {
                for (const strand of curriculumTree.strands) {
                    if (strand.sub_strands) {
                        for (const ss of strand.sub_strands) {
                            // Match by name
                            if (lesson.sub_strand.includes(ss.name)) {
                                subStrandId = ss.id;
                                break;
                            }
                        }
                    }
                    if (subStrandId) break;
                }
            }

            if (!subStrandId) {
                showToast('Could not find official curriculum Sub-Strand matching this lesson.', 'warning');
                setSubmitting(false);
                setGeneratingLessonId(null);
                return;
            }

            const response = await post('/api/cbc/lesson-plans/generate_from_substrand/', {
                sub_strand_id: subStrandId,
                week_number: weekNum,
                title: lesson.title || `Week ${weekNum} Lesson: ${lesson.sub_strand}`,
                sow_row_data: lesson
            });

            if (response.success && response.lesson_plan) {
                showToast(`Lesson plan generated successfully`, 'success');
                // Redirect to the full editor page
                navigate(`/teacher/lesson-plan/editor/${response.lesson_plan.id}`);
            } else {
                showToast(response.message || 'Failed to generate plan', 'error');
            }
        } catch (error) {
            console.error('Error generating lesson plan:', error);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to generate lesson plan due to server error.';
            showToast(errorMsg, 'error');
        } finally {
            setSubmitting(false);
            setGeneratingLessonId(null);
        }
    };

    const handleDeleteLesson = (lessonId) => {
        setLessonToDelete(lessonId);
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteLesson = async () => {
        if (!lessonToDelete) return;

        const updatedEdits = roadmapEdits.filter(l => l.$$id !== lessonToDelete);
        setRoadmapEdits(updatedEdits);
        await handleSaveRoadmap(updatedEdits);

        setShowDeleteConfirmation(false);
        setLessonToDelete(null);
    };

    const handleUpdateLesson = (id, field, value) => {
        setRoadmapEdits(prev => prev.map(l => l.$$id === id ? { ...l, [field]: value } : l));
    };

    const handleSaveRoadmap = async (customEdits = null) => {
        if (readOnly || isHOS) return;

        try {
            setSubmitting(true);
            const editsToSave = customEdits || roadmapEdits;

            if (teacherMode && learningAreaId) {
                // Teacher mode: save to teacher-specific override endpoint
                const payload = {
                    learning_area: learningAreaId,
                    academic_term: selectedScheme.academic_term?.id || selectedScheme.academic_term,
                    weekly_breakdown: editsToSave.map(({ id, $$id, ...rest }) => ({
                        ...rest,
                        week: rest.week_number
                    }))
                };

                const res = await post('/api/cbc/schemes-of-work/teacher-sow/', payload);
                const savedData = res.data;

                // Update global cache
                const cacheKey = `${learningAreaId}`;
                sowGlobalCache.teacherSow[cacheKey] = savedData;

                // Directly update state to avoid full page reload
                setSelectedScheme(savedData);
                showToast('Scheme of Work updated successfully!', 'success');
                setIsEditingRoadmap(false);
                // No await fetchData() here, preventing the loading spinner flash
            } else {
                // Admin mode: update master SOW
                const payload = {
                    ...selectedScheme,
                    academic_term: selectedScheme.academic_term.id || selectedScheme.academic_term,
                    learning_area: selectedScheme.learning_area.id || selectedScheme.learning_area,
                    weekly_breakdown: editsToSave.map(({ id, $$id, ...rest }) => ({
                        ...rest,
                        week: rest.week_number
                    }))
                };
                const updatedScheme = await put(`/api/cbc/schemes-of-work/${selectedScheme.id}/`, payload);
                // Ensure id is preserved in state even if response is partial
                const fullScheme = { ...updatedScheme, id: updatedScheme.id || selectedScheme.id };
                setSelectedScheme(fullScheme);
                showToast('Scheme of Work updated successfully!', 'success');

                // Optimization: Prepare curriculum options lists ONCE
                const sOptions = [...new Set(curriculumTree?.strands?.map(s => s.name) || [])];
                const ssOptions = [...new Set(curriculumTree?.strands?.flatMap(s => s.sub_strands || [])?.map(ss => ss.name) || [])];
                const loOptions = [...new Set(curriculumTree?.strands?.flatMap(s => s.sub_strands || [])?.flatMap(ss => ss.learning_outcomes || [])?.map(lo => lo.description) || [])];

                // Map existing data to curriculum items with context
                const mappedEdits = (updatedScheme.weekly_breakdown || []).map(w => ({
                    ...w,
                    week_number: w.week_number || w.week || 1,
                    $$id: Math.random().toString(36).substr(2, 9),
                    strand: mapItemsToCurriculum(w.strand, sOptions),
                    sub_strand: mapItemsToCurriculum(w.sub_strand, ssOptions),
                    specific_outcomes: mapItemsToCurriculum(w.specific_outcomes, loOptions)
                }));

                setRoadmapEdits(mappedEdits);
                setIsEditingRoadmap(false);
                // Removed await fetchData() to prevent full page reload
            }
        } catch (err) {
            console.error('Error saving roadmap:', err);
            const errorMsg = err.response?.data?.error || err.data?.error || err.message || 'Failed to save roadmap changes.';
            showToast(errorMsg, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // const filteredSchemes = schemes.filter(scheme => {
    //     const matchesTerm = !filterTerm || scheme.academic_term === parseInt(filterTerm);
    //     const matchesArea = !filterArea || scheme.learning_area === parseInt(filterArea);
    //     return matchesTerm && matchesArea;
    // });

    const renderImportModal = () => {
        if (!showImportModal) return null;
        return (
            <Modal
                isOpen={showImportModal}
                onClose={() => { if (!importing) { setShowImportModal(false); setImportError(null); setImportFile(null); } }}
                title="Import Scheme of Work"
                variant="compact"
                maxWidth="max-w-xl"
            >
                <div className="space-y-4">
                    <div className="border-2 border-dashed border-amber-300 hover:border-amber-500 rounded-2xl p-6 text-center transition-all bg-amber-50/20">
                        <input
                            type="file"
                            accept=".pdf,.docx"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setImportFile(file);
                                    setImportError(null);
                                }
                            }}
                            className="hidden"
                            id="sow-file-upload"
                            disabled={importing}
                        />
                        <label htmlFor="sow-file-upload" className="cursor-pointer flex flex-col items-center gap-2">
                            <ArrowUpTrayIcon className="w-8 h-8 text-amber-500" />
                            <span className="text-xs font-bold text-slate-700">
                                {importFile ? importFile.name : 'Click to choose PDF or DOCX file'}
                            </span>
                            <span className="text-[10px] text-slate-400">Maximum 14 weeks per document</span>
                        </label>
                    </div>

                    {importError && (
                        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-600 flex items-center gap-2">
                            <span>⚠️</span>
                            <span>{importError}</span>
                        </div>
                    )}

                    {importing && (
                        <div className="p-5 bg-[#18216D]/5 border border-[#18216D]/10 rounded-2xl space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-black text-[#18216D] uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#FFD700] animate-ping" />
                                    Importing Scheme of Work...
                                </span>
                                <span className="text-xs font-black text-[#18216D] font-mono">
                                    {importStep === 1 ? '25%' : importStep === 2 ? '55%' : importStep === 3 ? '85%' : '95%'}
                                </span>
                            </div>

                            <div className="w-full bg-[#18216D]/10 h-1.5 rounded-full overflow-hidden">
                                <div
                                    className="bg-[#FFD700] h-full transition-all duration-700 rounded-full"
                                    style={{ width: `${importStep === 1 ? 25 : importStep === 2 ? 55 : importStep === 3 ? 85 : 95}%` }}
                                />
                            </div>

                            <p className="text-xs font-bold text-[#18216D] transition-all">
                                {importStep === 1 && 'Parsing document structure...'}
                                {importStep === 2 && 'Extracting weekly lessons with AI...'}
                                {importStep === 3 && 'Matching CBC curriculum registry...'}
                                {importStep === 4 && 'Finalizing Scheme of Work roadmap...'}
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                        <button
                            onClick={() => { setShowImportModal(false); setImportError(null); setImportFile(null); }}
                            disabled={importing}
                            className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleExecuteImport}
                            disabled={!importFile || importing}
                            className="px-5 py-2 bg-[#FFD700] hover:bg-[#e6c200] text-[#18216D] rounded-xl text-xs font-black uppercase tracking-wider shadow-md hover:opacity-95 disabled:opacity-50 flex items-center gap-2"
                        >
                            <ArrowUpTrayIcon className="w-4 h-4 text-[#18216D]" />
                            {importing ? 'Importing...' : 'Upload & Import'}
                        </button>
                    </div>
                </div>
            </Modal>
        );
    };

    if (loading) {
        return <LoadingSpinner fullScreen />;
    }

    if (!selectedScheme && teacherMode) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-12 text-center animate-in fade-in zoom-in-95 duration-500 m-8">
                <BookOpenIcon className="w-20 h-20 text-indigo-100 mb-6" />
                <h2 className="text-2xl font-black text-[#18216D] mb-3 tracking-tight">No Scheme of Work Found</h2>
                <p className="text-sm text-slate-500 max-w-md mx-auto mb-8 leading-relaxed">
                    You haven't created a Scheme of Work for this learning area in the current term. Initialize your roadmap to start adding weekly lesson plans.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                    <button
                        onClick={async () => {
                            try {
                                setSubmitting(true);
                                const activeTerm = terms.find(t => t.is_current)?.id || (terms.length > 0 ? terms[0].id : null);
                                if (!activeTerm) {
                                    alert("No active academic term found. Please create one in settings.");
                                    return;
                                }
                                const initialRowId = Math.random().toString(36).substr(2, 9);
                                const initialWeeklyBreakdown = [{
                                    $$id: initialRowId,
                                    week_number: 1,
                                    lesson_number: 1,
                                    lesson_title: '',
                                    strand: '',
                                    sub_strand: '',
                                    specific_outcomes: '',
                                    key_inquiry_questions: '',
                                    learning_experiences: '',
                                    resources: '',
                                    assessment: ''
                                }];
                                await post('/api/cbc/schemes-of-work/teacher-sow/', {
                                    learning_area: learningAreaId,
                                    academic_term: activeTerm,
                                    weekly_breakdown: initialWeeklyBreakdown
                                });
                                // Invalidate cache & reload
                                const cacheKey = `${learningAreaId}`;
                                delete sowGlobalCache.teacherSow[cacheKey];
                                await fetchData();
                                setRowEditingId(initialRowId);
                            } catch (e) {
                                console.error('Initialize SOW error:', e);
                                const errorMsg = e.response?.data?.error || e.data?.error || e.message || "Failed to initialize scheme.";
                                showToast(errorMsg, 'error');
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                        disabled={submitting}
                        className="px-8 py-3 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-[0.15em] shadow-lg shadow-indigo-200 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-3 disabled:opacity-50"
                    >
                        {submitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Initializing...
                            </>
                        ) : (
                            <>
                                <PlusIcon className="w-5 h-5" />
                                Create Schemes of work
                            </>
                        )}
                    </button>

                </div>

                {/* Ensure Modal renders in empty state view */}
                {showImportModal && renderImportModal()}
            </div>
        );
    }

    const getRowEditorOptions = (lesson) => {
        // Hierarchical options for dropdowns (ID-First strategy)
        const strandOptions = curriculumTree?.strands?.map(s => s.name) || [];
        const selectedStrands = Array.isArray(lesson.strand) ? lesson.strand : (lesson.strand ? [lesson.strand] : []);

        // 1. Resolve registry objects using ID if available
        let registrySubStrand = null;
        if (lesson.sub_strand_id && curriculumTree?.strands) {
            for (const s of curriculumTree.strands) {
                if (s.sub_strands) {
                    const found = s.sub_strands.find(ss => String(ss.id) === String(lesson.sub_strand_id));
                    if (found) { registrySubStrand = found; break; }
                }
            }
        }

        const selectedSubStrands = Array.isArray(lesson.sub_strand) ? lesson.sub_strand : (lesson.sub_strand ? [lesson.sub_strand] : []);
        // Fallback to name matching if no ID available
        if (!registrySubStrand) {
            registrySubStrand = (curriculumTree?.strands || [])
                .flatMap(s => s.sub_strands || [])
                .find(ss => selectedSubStrands.includes(ss.name) || selectedSubStrands.some(sel => sel && ss.name && sel.trim().toLowerCase() === ss.name.trim().toLowerCase()));
        }

        const subStrandOptions = (curriculumTree?.strands || [])
            .filter(s => selectedStrands.includes(s.name))
            .flatMap(s => s.sub_strands || [])
            .map(ss => ss.name);

        // 2. Build options based on Registry Object (Primary) or Text Matching (Fallback)
        let outcomeOptions = registrySubStrand
            ? (registrySubStrand.learning_outcomes || []).map(lo => lo.description)
            : (curriculumTree?.strands || [])
                .filter(s => selectedStrands.some(rs => rs.toUpperCase() === s.name.toUpperCase()))
                .flatMap(s => s.sub_strands || [])
                .filter(ss => selectedSubStrands.some(rss => rss.toUpperCase() === ss.name.toUpperCase()))
                .flatMap(ss => ss.learning_outcomes || [])
                .map(lo => lo.description);

        if (lesson.imported_outcomes && Array.isArray(lesson.imported_outcomes)) {
            outcomeOptions = [...new Set([...outcomeOptions, ...lesson.imported_outcomes])];
        }

        let experienceOptions = [...new Set([
            ...(registrySubStrand
                ? (registrySubStrand.registry_experiences || []).map(e => e.description)
                : (curriculumTree?.strands || [])
                    .filter(s => selectedStrands.some(rs => rs.toUpperCase() === s.name.toUpperCase()))
                    .flatMap(s => s.sub_strands || [])
                    .filter(ss => selectedSubStrands.some(rss => rss.toUpperCase() === ss.name.toUpperCase()))
                    .flatMap(ss => (ss.registry_experiences || []).map(e => e.description))
            ),
            ...(registrySubStrand
                ? (registrySubStrand.learning_outcomes || []).flatMap(lo => (lo.experiences || []).map(e => e.description))
                : (curriculumTree?.strands || [])
                    .filter(s => selectedStrands.some(rs => rs.toUpperCase() === s.name.toUpperCase()))
                    .flatMap(s => s.sub_strands || [])
                    .filter(ss => selectedSubStrands.some(rss => rss.toUpperCase() === ss.name.toUpperCase()))
                    .flatMap(ss => ss.learning_outcomes || [])
                    .flatMap(lo => (lo.experiences || []).map(e => e.description))
            )
        ].map(a => a?.trim()).filter(Boolean))];

        if (lesson.imported_experiences && Array.isArray(lesson.imported_experiences)) {
            experienceOptions = [...new Set([...experienceOptions, ...lesson.imported_experiences])];
        }

        return { strandOptions, subStrandOptions, outcomeOptions, experienceOptions, registrySubStrand, selectedStrands, selectedSubStrands };
    };

    const renderRoadmap = (scheme, isViewOnly = false) => {
        // Group lessons by week number
        const lessonsByWeek = (roadmapEdits || []).reduce((acc, current) => {
            const week = current.week_number || current.week || 1;
            if (!acc[week]) acc[week] = [];
            acc[week].push(current);
            return acc;
        }, {});

        const sortedWeeks = Object.keys(lessonsByWeek).sort((a, b) => parseInt(a) - parseInt(b));

        // Calculate term duration to flag overflow weeks
        const termObj = terms.find(t => t.id === (scheme.academic_term?.id || scheme.academic_term));
        const maxWeeks = termObj ? Math.ceil((new Date(termObj.end_date) - new Date(termObj.start_date)) / (1000 * 60 * 60 * 24 * 7)) : 12;

        const renderAccordionView = () => {
            return (
                <div className="space-y-4">
                    {sortedWeeks.map((weekNum) => {
                        const lessons = lessonsByWeek[weekNum];
                        const isOverflow = parseInt(weekNum) > maxWeeks;

                        return (
                            <div key={`week-${weekNum}`} className={`bg-white rounded-2xl border ${isOverflow ? 'border-rose-200' : 'border-slate-200'} shadow-sm overflow-hidden`}>
                                <div
                                    className={`px-6 py-4 flex items-center justify-between cursor-pointer transition-colors ${isOverflow ? 'bg-rose-50 hover:bg-rose-100' : 'bg-slate-50 hover:bg-slate-100/80'}`}
                                    onClick={() => toggleWeek(weekNum)}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${collapsedWeeks.has(weekNum) ? 'bg-white text-slate-400 shadow-sm' : 'bg-[#18216D] text-white shadow-md'}`}>
                                            <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${collapsedWeeks.has(weekNum) ? '-rotate-90' : ''}`} />
                                        </div>
                                        <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-[#18216D] font-black">
                                            W{weekNum}
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold text-[#18216D]">Week {weekNum}</h3>
                                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{lessons.length} Lessons</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {!isViewOnly && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleAddWeekBelow(parseInt(weekNum)); }}
                                                className="px-4 py-2 bg-white text-[#18216D] border border-slate-200 rounded-lg text-xs font-bold uppercase tracking-widest hover:border-[#18216D] transition-all flex items-center gap-2 shadow-sm"
                                            >
                                                <PlusIcon className="w-3.5 h-3.5" />
                                                Add Week
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {!collapsedWeeks.has(weekNum) && (
                                    <div className="p-4 space-y-4 border-t border-slate-100 bg-slate-50/30">
                                        {(() => {
                                            const lessonDisplayData = computeLessonDisplayData(lessons);
                                            return lessons.map((lesson, lessonIndex) => {
                                                const displayParts = lessonDisplayData[lessonIndex];
                                            const isLocalEditing = rowEditingId === lesson.$$id;
                                            const isRowEditable = isEditingRoadmap || isLocalEditing;

                                            const { strandOptions, subStrandOptions, outcomeOptions, experienceOptions, registrySubStrand } = getRowEditorOptions(lesson);

                                            if (isLocalEditing) {
                                                return (
                                                    <RowEditorPanel
                                                        key={lesson.$$id || lessonIndex}
                                                        lesson={lesson}
                                                        lessonIndex={lessonIndex}
                                                        weekNum={weekNum}
                                                        strandOptions={strandOptions}
                                                        subStrandOptions={subStrandOptions}
                                                        outcomeOptions={outcomeOptions}
                                                        experienceOptions={experienceOptions}
                                                        registrySubStrand={registrySubStrand}
                                                        handleUpdateLesson={handleUpdateLesson}
                                                        setRowEditingId={setRowEditingId}
                                                        handleSaveRoadmap={handleSaveRoadmap}
                                                        setEditorModal={setEditorModal}
                                                        teacherMode={teacherMode}
                                                        isTable={false}
                                                    />
                                                );
                                            }

                                            return (
                                                <div key={lesson.$$id || lessonIndex} className="bg-white rounded-[1.5rem] border border-slate-100 hover:border-[#18216D]/20 hover:shadow-xl hover:shadow-indigo-900/5 transition-all p-5">
                                                    <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-100 gap-4">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            <span className="px-3 py-1.5 bg-[#18216D]/5 text-[#18216D] border border-[#18216D]/15 shadow-xs rounded-lg text-[10px] font-black uppercase tracking-widest flex-shrink-0">
                                                                {displayParts.map(p => `Lesson ${p}`).join(', ')}
                                                            </span>
                                                            <h4 className="text-sm sm:text-base font-black text-[#18216D] tracking-tight truncate">
                                                                {lesson.lesson_title || lesson.title || 'Untitled Lesson'}
                                                            </h4>
                                                        </div>
                                                        <div className="flex items-center gap-3 flex-shrink-0">
                                                            {teacherMode && (() => {
                                                                const lessonKey = lesson.$$id || lesson.id || `${weekNum}-${lesson.sub_strand}`;
                                                                const isGen = generatingLessonId === lessonKey;
                                                                return (
                                                                    <SOWTooltip text={isGen ? "Generating Lesson Plan..." : "Generate Lesson Plan"}>
                                                                        <button
                                                                            disabled={isGen}
                                                                            onClick={(e) => { e.stopPropagation(); handleGenerateLessonPlanFromSOW(lesson, weekNum); }}
                                                                            className={`px-3 py-2 rounded-xl transition-all border shadow-sm flex items-center gap-2 ${
                                                                                isGen 
                                                                                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300 cursor-wait animate-pulse' 
                                                                                    : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white border-emerald-100 hover:shadow-emerald-100 active:scale-95 group'
                                                                            }`}
                                                                        >
                                                                            {isGen ? (
                                                                                <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                                                            ) : (
                                                                                <BookOpenIcon className="w-4 h-4" />
                                                                            )}
                                                                            <span className="text-[9px] font-black uppercase tracking-widest hidden sm:inline">
                                                                                {isGen ? 'Generating...' : 'Lesson Plan'}
                                                                            </span>
                                                                        </button>
                                                                    </SOWTooltip>
                                                                );
                                                            })()}
                                                            {!isViewOnly && (
                                                                <div className="flex items-center gap-2 border-l border-slate-100 pl-3">
                                                                    <SOWTooltip text="Add Lesson">
                                                                        <button
                                                                            onClick={() => handleAddLessonToWeek(parseInt(weekNum), lesson.$$id)}
                                                                            className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-200/80 text-[#18216D] hover:bg-[#18216D] hover:text-white shadow-sm flex items-center justify-center transition-all"
                                                                        >
                                                                            <PlusIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                                        </button>
                                                                    </SOWTooltip>
                                                                    {isRowEditable ? (
                                                                        <button
                                                                            onClick={() => setRowEditingId(null)}
                                                                            className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-sm"
                                                                        >
                                                                            Done
                                                                        </button>
                                                                    ) : (
                                                                        <SOWTooltip text="Edit Lesson">
                                                                            <button
                                                                                onClick={() => setRowEditingId(lesson.$$id)}
                                                                                className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200/80 text-amber-700 hover:bg-amber-500 hover:text-white shadow-sm flex items-center justify-center transition-all"
                                                                            >
                                                                                <PencilIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                                            </button>
                                                                        </SOWTooltip>
                                                                    )}
                                                                    <SOWTooltip text="Delete Lesson">
                                                                        <button
                                                                            onClick={() => handleDeleteLesson(lesson.$$id)}
                                                                            className="w-8 h-8 rounded-lg bg-red-50 border border-red-200/80 text-red-600 hover:bg-red-600 hover:text-white shadow-sm flex items-center justify-center transition-all"
                                                                        >
                                                                            <TrashIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                                        </button>
                                                                    </SOWTooltip>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div>
                                                                <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Strand</label>
                                                                {isRowEditable ? (
                                                                    <SearchableDropdown
                                                                        options={(scheme.strands || []).map(s => s.name)}
                                                                        value={lesson.strand}
                                                                        onChange={(val) => handleUpdateLesson(lesson.$$id, 'strand', val)}
                                                                        placeholder="Select Strand..."
                                                                    />
                                                                ) : (
                                                                    <p className="text-sm font-semibold text-[#18216D]">{lesson.strand || '-'}</p>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Sub-strand</label>
                                                                {isRowEditable ? (
                                                                    <SearchableDropdown
                                                                        options={(() => {
                                                                            const strand = (scheme.strands || []).find(s => s.name === lesson.strand);
                                                                            return strand?.sub_strands?.map(ss => ss.name) || [];
                                                                        })()}
                                                                        value={lesson.sub_strand}
                                                                        onChange={(val) => handleUpdateLesson(lesson.$$id, 'sub_strand', val)}
                                                                        placeholder="Select Sub-strand..."
                                                                        disabled={!lesson.strand}
                                                                    />
                                                                ) : (
                                                                    <p className="text-sm font-semibold text-[#18216D]">{Array.isArray(lesson.sub_strand) ? lesson.sub_strand.join(', ') : (lesson.sub_strand || '-')}</p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 gap-4">
                                                            <div>
                                                                <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Learning Outcomes</label>
                                                                {isRowEditable ? (
                                                                    <textarea
                                                                        value={Array.isArray(lesson.specific_outcomes) ? lesson.specific_outcomes.join('\n') : (lesson.specific_outcomes || '')}
                                                                        onChange={(e) => handleUpdateLesson(lesson.$$id, 'specific_outcomes', e.target.value)}
                                                                        className="w-full min-h-[80px] p-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-xl focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none resize-y"
                                                                    />
                                                                ) : (
                                                                    <div className="space-y-1.5">
                                                                        {(lesson.specific_outcomes && (Array.isArray(lesson.specific_outcomes) ? lesson.specific_outcomes.length > 0 : lesson.specific_outcomes)) ? (
                                                                            (Array.isArray(lesson.specific_outcomes)
                                                                                ? lesson.specific_outcomes
                                                                                : (lesson.specific_outcomes ? [lesson.specific_outcomes] : [])
                                                                            ).map((desc, i) => (
                                                                                <div key={i} className="flex gap-2 items-start">
                                                                                    <div className="w-3 h-3 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-1">
                                                                                        <CheckCircleIcon className="w-2 h-2 text-emerald-600" />
                                                                                    </div>
                                                                                    <span className="text-[11px] font-bold text-slate-700 leading-relaxed">{desc}</span>
                                                                                </div>
                                                                            ))
                                                                        ) : null}
                                                                        
                                                                        {lesson.imported_outcomes && lesson.imported_outcomes.length > 0 && (
                                                                            <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5">
                                                                                <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] block mb-1">Imported Outcomes</span>
                                                                                {lesson.imported_outcomes.map((desc, i) => (
                                                                                    <div key={`imp-out-${i}`} className="flex gap-2 items-start">
                                                                                        <div className="w-3 h-3 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 mt-1">
                                                                                            <DocumentTextIcon className="w-2 h-2 text-[#18216D]" />
                                                                                        </div>
                                                                                        <span className="text-[11px] leading-relaxed text-slate-600 font-bold">{desc}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {(!lesson.specific_outcomes || lesson.specific_outcomes.length === 0) && (!lesson.imported_outcomes || lesson.imported_outcomes.length === 0) && (
                                                                            <span className="italic text-slate-300 text-[10px] font-black uppercase tracking-widest">—</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Experiences</label>
                                                                    {isRowEditable ? (
                                                                        <textarea
                                                                            value={Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences.join('\n') : (lesson.learning_experiences || '')}
                                                                            onChange={(e) => handleUpdateLesson(lesson.$$id, 'learning_experiences', e.target.value)}
                                                                            className="w-full min-h-[80px] p-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-xl outline-none resize-y"
                                                                        />
                                                                    ) : (
                                                                        <div className="space-y-1">
                                                                            {(lesson.learning_experiences && (Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences.length > 0 : lesson.learning_experiences)) ? (
                                                                                (Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences : [lesson.learning_experiences]).map((desc, i) => (
                                                                                    <div key={i} className="flex gap-1.5 items-start">
                                                                                        <BookOpenIcon className="w-3 h-3 text-[#18216D] flex-shrink-0 mt-0.5" />
                                                                                        <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                    </div>
                                                                                ))
                                                                            ) : null}
                                                                            
                                                                            {lesson.imported_experiences && lesson.imported_experiences.length > 0 && (
                                                                                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                                                                                    <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] block mb-1">Imported Experiences</span>
                                                                                    {lesson.imported_experiences.map((desc, i) => (
                                                                                        <div key={`imp-exp-${i}`} className="flex gap-1.5 items-start">
                                                                                            <LightBulbIcon className="w-3 h-3 text-teal-600 flex-shrink-0 mt-0.5" />
                                                                                            <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {(!lesson.learning_experiences || lesson.learning_experiences.length === 0) && (!lesson.imported_experiences || lesson.imported_experiences.length === 0) && (
                                                                                <span className="text-[10px] text-slate-300 italic">—</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Resources</label>
                                                                    {isRowEditable ? (
                                                                        <textarea
                                                                            value={Array.isArray(lesson.resources) ? lesson.resources.join('\n') : (lesson.resources || '')}
                                                                            onChange={(e) => handleUpdateLesson(lesson.$$id, 'resources', e.target.value)}
                                                                            className="w-full min-h-[80px] p-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-xl outline-none resize-y"
                                                                        />
                                                                    ) : (
                                                                        <div className="text-[11px] font-bold text-slate-600 whitespace-pre-line">{Array.isArray(lesson.resources) ? lesson.resources.map(item => `• ${item}`).join('\n') : (lesson.resources || '-')}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Key Inquiry Questions</label>
                                                                    {isRowEditable ? (
                                                                        <textarea
                                                                            value={Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions.join('\n') : (lesson.key_inquiry_questions || '')}
                                                                            onChange={(e) => handleUpdateLesson(lesson.$$id, 'key_inquiry_questions', e.target.value)}
                                                                            className="w-full min-h-[60px] p-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-xl outline-none resize-y"
                                                                        />
                                                                    ) : (
                                                                        <div className="space-y-1">
                                                                            {(lesson.key_inquiry_questions && (Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions.length > 0 : lesson.key_inquiry_questions)) ? (
                                                                                (Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions : [lesson.key_inquiry_questions]).map((desc, i) => (
                                                                                    <div key={i} className="flex gap-1.5 items-start">
                                                                                        <span className="text-[11px] font-bold text-amber-500 flex-shrink-0 mt-0.5">?</span>
                                                                                        <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                    </div>
                                                                                ))
                                                                            ) : null}
                                                                            
                                                                            {lesson.imported_inquiry_questions && lesson.imported_inquiry_questions.length > 0 && (
                                                                                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                                                                                    <span className="text-[8px] font-black text-[#18216D] uppercase tracking-[0.2em] block mb-1">Imported Questions</span>
                                                                                    {lesson.imported_inquiry_questions.map((desc, i) => (
                                                                                        <div key={`imp-inq-${i}`} className="flex gap-1.5 items-start">
                                                                                            <span className="text-amber-500 font-black text-[11px] flex-shrink-0 mt-0.5">?</span>
                                                                                            <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                            
                                                                            {(!lesson.key_inquiry_questions || lesson.key_inquiry_questions.length === 0) && (!lesson.imported_inquiry_questions || lesson.imported_inquiry_questions.length === 0) && (
                                                                                <span className="text-[10px] text-slate-300 italic">—</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5">Assessment</label>
                                                                    {isRowEditable ? (
                                                                        <textarea
                                                                            value={Array.isArray(lesson.assessment) ? lesson.assessment.join('\n') : (lesson.assessment || '')}
                                                                            onChange={(e) => handleUpdateLesson(lesson.$$id, 'assessment', e.target.value)}
                                                                            className="w-full min-h-[60px] p-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-xl outline-none resize-y"
                                                                        />
                                                                    ) : (
                                                                        <div className="text-[11px] font-bold text-slate-600 whitespace-pre-line">{Array.isArray(lesson.assessment) ? lesson.assessment.map(item => `• ${item}`).join('\n') : (lesson.assessment || '-')}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                        })()}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <div className={isFullScreen ? "relative" : "space-y-6"}>
                {isFullScreen ? (
                    <div className="bg-white border-b border-slate-100 sticky top-[64px] lg:top-0 z-30 shadow-sm px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={onBack}
                                className="p-2.5 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-[#18216D] transition-all border border-transparent hover:border-slate-100 flex items-center gap-2 group"
                            >
                                <svg className="w-4 h-4 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                                </svg>
                            </button>
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">Schemes of Work</p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <h1 className="text-base font-black text-[#18216D] tracking-tight leading-none">{scheme.learning_area_name || scheme.learning_area?.name}</h1>
                                    <span className="inline-flex items-center px-2 py-0.5 bg-amber-50 text-[#18216D] rounded-full text-[9px] font-black uppercase tracking-widest border border-amber-200/60 leading-none">
                                        {scheme.term_name}
                                    </span>
                                    {isHOS && (teacherName || scheme.created_by_name) && (
                                        <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-[9px] font-black uppercase tracking-widest border border-indigo-100 leading-none">
                                            {teacherName || scheme.created_by_name}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {!isHOS && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleExportPDF(scheme)}
                                        className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-sm"
                                    >
                                        <DocumentArrowDownIcon className="w-3.5 h-3.5 text-white" />
                                        Export PDF
                                    </button>
                                </div>
                            )}
                            {!isHOS && (
                                <button
                                    onClick={() => handleQuickAddWeek(scheme)}
                                    disabled={submitting}
                                    className="px-4 py-2 bg-[#18216D] text-white rounded-xl font-black text-[9px] uppercase tracking-widest hover:opacity-90 transition-all flex items-center gap-2 group shadow-sm"
                                >
                                    <PlusIcon className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform" />
                                    Add Week
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4">
                        <div className="flex items-center gap-4">
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em] mb-0.5">Schemes of Work</p>
                                <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                    {scheme.learning_area_name || scheme.learning_area?.name}
                                </h2>
                                <span className="inline-flex items-center px-2.5 py-0.5 mt-1.5 bg-amber-50 text-[#18216D] rounded-full text-[9px] font-black uppercase tracking-widest border border-amber-200/60">
                                    {scheme.term_name}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            {!isHOS && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowActionModal(v => !v)}
                                        className="flex items-center gap-2 px-3.5 py-2 bg-[#FFD700] hover:bg-[#e6c200] text-[#18216D] rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-sm"
                                    >
                                        <i className="fas fa-ellipsis-v text-[10px] text-[#18216D]" />
                                        Actions
                                    </button>
                                    {showActionModal && (
                                        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                            <button
                                                onClick={() => { handleExportPDF(scheme); setShowActionModal(false); }}
                                                className="w-full flex items-center gap-3 px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-emerald-700 hover:bg-emerald-50 transition-colors"
                                            >
                                                <DocumentArrowDownIcon className="w-4 h-4 text-emerald-500" />
                                                Export PDF
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {teacherMode && !isFullScreen && (
                                <button
                                    onClick={() => navigate(`/teacher/scheme-of-work/${learningAreaId}`, {
                                        state: { learningAreaId, courseName: scheme.learning_area_name || scheme.learning_area?.name }
                                    })}
                                    className="flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-white border border-slate-100 hover:border-indigo-100 text-slate-400 hover:text-[#18216D] rounded-xl text-[9px] font-black uppercase tracking-widest transition-all group shadow-sm"
                                    title="Open Full Screen"
                                >
                                    <ArrowsPointingOutIcon className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                    Full Screen
                                </button>
                            )}

                            <div className="flex gap-2">
                                <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-200">
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${viewMode === 'table' ? 'bg-[#18216D] text-white shadow-md' : 'text-slate-400 hover:text-[#18216D]'}`}
                                    >
                                        <TableCellsIcon className="w-3.5 h-3.5" />
                                        Table
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-[#18216D] text-white shadow-md' : 'text-slate-400 hover:text-[#18216D]'}`}
                                    >
                                        <ListBulletIcon className="w-3.5 h-3.5" />
                                        List
                                    </button>
                                </div>
                                {!isHOS && !isViewOnly && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleQuickAddWeek(scheme)}
                                            disabled={submitting}
                                            className="px-4 py-2 bg-[#18216D] text-white rounded-xl font-black text-[9px] uppercase tracking-widest hover:opacity-90 transition-all flex items-center gap-2 group shadow-sm"
                                        >
                                            <PlusIcon className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform" />
                                            Add Week
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className={`${isFullScreen ? (viewMode === 'list' ? "p-6 md:p-10 max-w-5xl mx-auto" : "p-2 md:p-6") : ""}`}>
                    {/* Horizontal Scroll Hint */}
                    <div className={`flex items-center justify-end mb-2 2xl:hidden ${viewMode === 'list' ? 'hidden' : ''}`}>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                            <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Swipe or scroll to view details
                            <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                        </span>
                    </div>

                    <div className={`relative overflow-hidden max-w-full ${viewMode === 'list' ? "" : "bg-white rounded-[2.5rem] border border-slate-100 shadow-xl p-2"}`}>
                        {/* Right edge fade for scroll affordance */}
                        <div className={`absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 2xl:hidden rounded-r-[2.5rem] ${viewMode === 'list' ? 'hidden' : ''}`}></div>

                        <div className="overflow-x-auto w-full custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
                            {viewMode === 'table' ? (
                                <table className="w-full text-left border-collapse min-w-[1300px] table-fixed">
                                    <thead>
                                        <tr className="bg-slate-50/80 border-b border-slate-100">
                                            <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] text-center w-24">Week</th>
                                            <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] w-[160px]">Strand</th>
                                            <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] w-[240px]">Lesson</th>
                                            <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] w-[280px]">Learning Outcomes</th>
                                            <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] w-[280px]">Experiences &amp; Resources</th>
                                            <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] w-[280px]">Enquiry &amp; Assessment</th>
                                            {!isHOS && <th className="px-5 py-4 text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] text-center w-[100px]">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50" ref={tableBodyRef}>
                                        {sortedWeeks.map((weekNum) => {
                                            const lessons = lessonsByWeek[weekNum];
                                            const isOverflow = parseInt(weekNum) > maxWeeks;
                                            const lessonDisplayData = computeLessonDisplayData(lessons);

                                            return (
                                                <React.Fragment key={`week-group-${weekNum}`}>
                                                    {lessons.map((lesson, lessonIndex) => {
                                                        const displayParts = lessonDisplayData[lessonIndex];
                                                const isLocalEditing = rowEditingId === lesson.$$id;
                                                const isRowEditable = isEditingRoadmap || isLocalEditing;
                                                const isNew = lastAddedRowId === lesson.$$id;
                                                const { strandOptions, subStrandOptions, outcomeOptions, experienceOptions, registrySubStrand, selectedStrands, selectedSubStrands } = getRowEditorOptions(lesson);

                                                return (
                                                    <React.Fragment key={`${weekNum}-${lesson.$$id || lessonIndex}`}>
                                                        <tr
                                                            className={`transition-all duration-300 group ${isOverflow ? 'bg-amber-50/20 hover:bg-amber-50/30' : 'hover:bg-slate-50/20'
                                                                } ${isLocalEditing ? 'bg-indigo-50/30 border-l-4 border-indigo-400' : ''
                                                                } ${isNew ? 'animate-in fade-in slide-in-from-top-4 duration-700 fill-mode-forwards' : ''
                                                                }`}
                                                        >
                                                            {lessonIndex === 0 && (
                                                                <td className="px-4 py-6 align-top text-center border-r border-slate-100/50" rowSpan={lessons.length}>
                                                                    <div className="flex flex-col items-center gap-1 sticky top-32">
                                                                        <span className={`text-[9px] font-black uppercase tracking-[0.2em] mb-1 ${isOverflow ? 'text-amber-400' : 'text-slate-300'}`}>
                                                                            {isOverflow ? 'EXT' : 'WK'}
                                                                        </span>
                                                                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl font-black text-base shadow-sm border ${isOverflow
                                                                            ? 'bg-amber-50 text-amber-600 border-amber-200'
                                                                            : 'bg-slate-50 text-[#18216D] border-slate-200'
                                                                            }`}>
                                                                            {weekNum}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            )}
                                                            {/* Strand cell — always read-only; local edit uses panel below */}
                                                            <td className="px-4 py-5 align-top">
                                                                {isRowEditable && !isLocalEditing ? (
                                                                    <div>
                                                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 block">Strand</span>
                                                                        <SearchableDropdown
                                                                            options={strandOptions.map((s, i) => ({ id: s, name: s }))}
                                                                            selectedValues={Array.isArray(lesson.strand) ? lesson.strand : (lesson.strand ? [lesson.strand] : [])}
                                                                            onChange={(val) => handleUpdateLesson(lesson.$$id, 'strand', [...(Array.isArray(lesson.strand) ? lesson.strand : (lesson.strand ? [lesson.strand] : [])).filter(v => v !== val), ...(Array.isArray(lesson.strand) ? lesson.strand : (lesson.strand ? [lesson.strand] : [])).includes(val) ? [] : [val]])}
                                                                            placeholder="Select Strand"
                                                                            isMultiple={true} compact={true}
                                                                            displayKey="name"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {[...new Set(selectedStrands)].map(s => (
                                                                            <span key={s} className="px-2 py-1 bg-indigo-50 text-[#18216D] rounded-lg text-[10px] font-black uppercase tracking-tight border border-indigo-100 break-words max-w-full">
                                                                                {s}
                                                                            </span>
                                                                        ))}
                                                                        {selectedStrands.length === 0 && <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest italic">—</span>}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* Sub-strand / Lesson — always read-only in local-edit mode */}
                                                            <td className="px-4 py-5 align-top">
                                                                {isRowEditable && !isLocalEditing ? (
                                                                    <div>
                                                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 block">Sub-Strand</span>
                                                                        <SearchableDropdown
                                                                            options={subStrandOptions.map(s => ({ id: s, name: s }))}
                                                                            selectedValues={Array.isArray(lesson.sub_strand) ? lesson.sub_strand : (lesson.sub_strand ? [lesson.sub_strand] : [])}
                                                                            onChange={(val) => {
                                                                                const current = Array.isArray(lesson.sub_strand) ? lesson.sub_strand : (lesson.sub_strand ? [lesson.sub_strand] : []);
                                                                                const updated = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
                                                                                handleUpdateLesson(lesson.$$id, 'sub_strand', updated);
                                                                            }}
                                                                            placeholder="Select Sub-Strand"
                                                                            isMultiple={true} compact={true}
                                                                            displayKey="name"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                            <span className="px-1.5 py-0.5 bg-[#18216D] text-white rounded-md text-[9px] font-black italic shrink-0">
                                                                                {displayParts.map(p => `L${p}`).join(', ')}
                                                                            </span>
                                                                            <span className="text-[11px] font-black text-[#18216D] leading-tight">
                                                                                {lesson.lesson_title || lesson.title || 'Untitled Lesson'}
                                                                            </span>
                                                                        </div>
                                                                        {[...new Set(selectedSubStrands)].map(ss => (
                                                                            <span key={ss} className="px-2 py-1 bg-slate-50 text-slate-700 rounded-lg text-[10px] font-bold border border-slate-200 break-words max-w-full w-full">
                                                                                {ss}
                                                                            </span>
                                                                        ))}
                                                                        {selectedSubStrands.length === 0 && <span className="text-[11px] font-black text-slate-700 leading-snug">{lesson.sub_strand}</span>}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            {/* Specific Outcomes — always read-only in local-edit mode */}
                                                            <td className="px-4 py-5 align-top">
                                                                {isRowEditable && !isLocalEditing ? (
                                                                    <div>
                                                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 block">Outcomes</span>
                                                                        <SearchableDropdown
                                                                            options={outcomeOptions.map(o => ({ id: o, name: o }))}
                                                                            selectedValues={[...(Array.isArray(lesson.specific_outcomes) ? lesson.specific_outcomes : (lesson.specific_outcomes ? [lesson.specific_outcomes] : [])), ...(lesson.imported_outcomes || [])]}
                                                                            onChange={(val) => {
                                                                                const currentSpecific = Array.isArray(lesson.specific_outcomes) ? lesson.specific_outcomes : (lesson.specific_outcomes ? [lesson.specific_outcomes] : []);
                                                                                const currentImported = lesson.imported_outcomes || [];
                                                                                
                                                                                if (currentImported.includes(val)) {
                                                                                    handleUpdateLesson(lesson.$$id, 'imported_outcomes', currentImported.filter(v => v !== val));
                                                                                } else if (currentSpecific.includes(val)) {
                                                                                    handleUpdateLesson(lesson.$$id, 'specific_outcomes', currentSpecific.filter(v => v !== val));
                                                                                } else {
                                                                                    handleUpdateLesson(lesson.$$id, 'specific_outcomes', [...currentSpecific, val]);
                                                                                }
                                                                            }}
                                                                            placeholder="Select Outcomes"
                                                                            isMultiple={true} compact={true}
                                                                            displayKey="name"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="space-y-1.5">
                                                                        {(lesson.specific_outcomes && (Array.isArray(lesson.specific_outcomes) ? lesson.specific_outcomes.length > 0 : lesson.specific_outcomes)) ? (
                                                                            (Array.isArray(lesson.specific_outcomes)
                                                                                ? lesson.specific_outcomes
                                                                                : (lesson.specific_outcomes ? [lesson.specific_outcomes] : [])
                                                                            ).map((desc, i) => (
                                                                                <div key={i} className="flex gap-2 items-start">
                                                                                    <div className="w-3 h-3 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-1">
                                                                                        <CheckCircleIcon className="w-2 h-2 text-emerald-600" />
                                                                                    </div>
                                                                                    <span className="text-[11px] font-bold text-slate-700 leading-relaxed">{desc}</span>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <span className="italic text-slate-300 text-[10px] font-black uppercase tracking-widest">—</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {/* Display Imported Outcomes if they exist */}
                                                                {lesson.imported_outcomes && lesson.imported_outcomes.length > 0 && (
                                                                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                                                                        {isRowEditable && !isLocalEditing && <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 block">Imported Outcomes</span>}
                                                                        {lesson.imported_outcomes.map((desc, i) => (
                                                                            <div key={i} className="flex gap-2 items-start group">
                                                                                <div className="w-3 h-3 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 mt-1">
                                                                                    <DocumentTextIcon className="w-2 h-2 text-slate-500" />
                                                                                </div>
                                                                                <span className="text-[11px] leading-relaxed text-slate-600 font-bold">{desc}</span>
                                                                                {isRowEditable && !isLocalEditing && (
                                                                                    <button 
                                                                                        onClick={() => {
                                                                                            const updated = lesson.imported_outcomes.filter((_, idx) => idx !== i);
                                                                                            handleUpdateLesson(lesson.$$id, 'imported_outcomes', updated);
                                                                                        }}
                                                                                        className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-red-500 transition-all rounded hover:bg-red-50"
                                                                                    >
                                                                                        <XMarkIcon className="w-3 h-3" />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                            </td>

                                                            {/* Experiences & Resources — always read-only in local-edit mode */}
                                                            <td className="px-4 py-5 align-top">
                                                                {isRowEditable && !isLocalEditing ? (
                                                                    <div className="space-y-3">
                                                                        <div>
                                                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 block">Experiences</span>
                                                                            <SearchableDropdown
                                                                                options={experienceOptions.map(o => ({ id: o, name: o }))}
                                                                                selectedValues={[...(Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences : (lesson.learning_experiences ? [lesson.learning_experiences] : [])), ...(lesson.imported_experiences || [])]}
                                                                                onChange={(val) => {
                                                                                    const currentSpecific = Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences : (lesson.learning_experiences ? [lesson.learning_experiences] : []);
                                                                                    const currentImported = lesson.imported_experiences || [];
                                                                                    
                                                                                    if (currentImported.includes(val)) {
                                                                                        handleUpdateLesson(lesson.$$id, 'imported_experiences', currentImported.filter(v => v !== val));
                                                                                    } else if (currentSpecific.includes(val)) {
                                                                                        handleUpdateLesson(lesson.$$id, 'learning_experiences', currentSpecific.filter(v => v !== val));
                                                                                    } else {
                                                                                        handleUpdateLesson(lesson.$$id, 'learning_experiences', [...currentSpecific, val]);
                                                                                    }
                                                                                }}
                                                                                placeholder="Select Experiences"
                                                                                isMultiple={true} compact={true}
                                                                                displayKey="name"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 block">Resources</span>
                                                                            <textarea
                                                                                rows="2"
                                                                                value={lesson.resources}
                                                                                onChange={(e) => handleUpdateLesson(lesson.$$id, 'resources', e.target.value)}
                                                                                onInput={autoResize}
                                                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-300 resize-none min-h-[44px] transition-all"
                                                                                placeholder="Materials needed..."
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="space-y-3">
                                                                        <div>
                                                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] block mb-1.5">Experiences</span>
                                                                            <div className="space-y-1">
                                                                                {(lesson.learning_experiences && (Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences.length > 0 : lesson.learning_experiences)) ? (
                                                                                    (Array.isArray(lesson.learning_experiences) ? lesson.learning_experiences : [lesson.learning_experiences]).map((desc, i) => (
                                                                                        <div key={i} className="flex gap-1.5 items-start">
                                                                                            <BookOpenIcon className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
                                                                                            <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                        </div>
                                                                                    ))
                                                                                ) : null}
                                                                                {lesson.imported_experiences && lesson.imported_experiences.length > 0 && (
                                                                                    <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                                                                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">Imported Experiences</span>
                                                                                        {lesson.imported_experiences.map((desc, i) => (
                                                                                            <div key={`imp-exp-${i}`} className="flex gap-1.5 items-start">
                                                                                                <LightBulbIcon className="w-3 h-3 text-teal-400 flex-shrink-0 mt-0.5" />
                                                                                                <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                                {(!lesson.learning_experiences || lesson.learning_experiences.length === 0) && (!lesson.imported_experiences || lesson.imported_experiences.length === 0) && (
                                                                                    <span className="text-[10px] text-slate-300 italic">—</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        {lesson.resources && (
                                                                            <div>
                                                                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] block mb-1">Resources</span>
                                                                                <p className="text-[11px] font-bold text-slate-600 leading-relaxed">{lesson.resources}</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* Enquiry & Assessment — always read-only in local-edit mode */}
                                                            <td className="px-4 py-5 align-top">
                                                                {isRowEditable && !isLocalEditing ? (
                                                                    <div className="space-y-3">
                                                                        <div>
                                                                            <div className="flex items-center justify-between mb-1.5">
                                                                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em]">Enquiry</span>
                                                                                <button
                                                                                    onClick={() => setEditorModal({ isOpen: true, field: 'key_inquiry_questions', lessonId: lesson.$$id, content: lesson.key_inquiry_questions || '', title: 'Edit Inquiry Questions' })}
                                                                                    className="text-[8px] font-black text-indigo-500 uppercase hover:underline"
                                                                                >
                                                                                    Advanced
                                                                                </button>
                                                                            </div>
                                                                            <textarea
                                                                                rows="2"
                                                                                value={typeof lesson.key_inquiry_questions === 'string' ? lesson.key_inquiry_questions : (Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions.join('\n') : '')}
                                                                                onChange={(e) => handleUpdateLesson(lesson.$$id, 'key_inquiry_questions', e.target.value)}
                                                                                onInput={autoResize}
                                                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-300 resize-none min-h-[44px] transition-all"
                                                                                placeholder="Inquiry questions (one per line)..."
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <div className="flex items-center justify-between mb-1.5">
                                                                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em]">Assessment</span>
                                                                                <button
                                                                                    onClick={() => setEditorModal({ isOpen: true, field: 'assessment', lessonId: lesson.$$id, content: lesson.assessment || '', title: 'Edit Assessment Methods' })}
                                                                                    className="text-[8px] font-black text-rose-500 uppercase hover:underline"
                                                                                >
                                                                                    Advanced
                                                                                </button>
                                                                            </div>
                                                                            <textarea
                                                                                rows="2"
                                                                                value={typeof lesson.assessment === 'string' ? lesson.assessment : (Array.isArray(lesson.assessment) ? lesson.assessment.join('\n') : '')}
                                                                                onChange={(e) => handleUpdateLesson(lesson.$$id, 'assessment', e.target.value)}
                                                                                onInput={autoResize}
                                                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-300 resize-none min-h-[44px] transition-all"
                                                                                placeholder="Assessment methods..."
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="space-y-3">
                                                                        {(lesson.key_inquiry_questions && (Array.isArray(lesson.key_inquiry_questions) ? lesson.key_inquiry_questions.length > 0 : lesson.key_inquiry_questions)) || (lesson.imported_inquiry_questions && lesson.imported_inquiry_questions.length > 0) ? (
                                                                            <div>
                                                                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] block mb-1.5">Enquiry</span>
                                                                                <div className="space-y-1">
                                                                                {(() => {
                                                                                    const inquiries = Array.isArray(lesson.key_inquiry_questions) 
                                                                                        ? lesson.key_inquiry_questions 
                                                                                        : (lesson.key_inquiry_questions ? lesson.key_inquiry_questions.split('\n') : []);
                                                                                    
                                                                                    return inquiries.map((q, i) => (
                                                                                        <div key={i} className="flex gap-1.5 items-start">
                                                                                            <span className="text-amber-400 font-black text-[10px] flex-shrink-0 mt-0.5">?</span>
                                                                                            <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{q}</span>
                                                                                        </div>
                                                                                    ));
                                                                                })()}
                                                                                {lesson.imported_inquiry_questions && lesson.imported_inquiry_questions.length > 0 && (
                                                                                    <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                                                                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">Imported Questions</span>
                                                                                        {lesson.imported_inquiry_questions.map((desc, i) => (
                                                                                            <div key={`imp-inq-tbl-${i}`} className="flex gap-1.5 items-start">
                                                                                                <span className="text-[11px] font-bold text-amber-500 flex-shrink-0 mt-0.5">?</span>
                                                                                                <span className="text-[11px] font-bold text-slate-600 leading-relaxed">{desc}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-[10px] text-slate-300 italic">—</span>
                                                                        )}
                                                                        {lesson.assessment && (
                                                                            <div>
                                                                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] block mb-1">Assessment</span>
                                                                                <p className="text-[11px] font-bold text-slate-600 leading-relaxed">
                                                                                    {typeof lesson.assessment === 'string' ? lesson.assessment : (Array.isArray(lesson.assessment) ? lesson.assessment.join(', ') : '')}
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                        {!lesson.key_inquiry_questions && !lesson.assessment && (
                                                                            <span className="text-[10px] text-slate-300 italic">—</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* Actions */}
                                                            {!isHOS && (
                                                                <td className="px-3 py-5 align-top bg-slate-50/10 border-l border-slate-100">
                                                                    <div className="flex flex-col items-center gap-2.5">
                                                                        {/* Generate Lesson Plan Icon */}
                                                                        {teacherMode && (() => {
                                                                            const lessonKey = lesson.$$id || lesson.id || `${weekNum}-${lesson.sub_strand}`;
                                                                            const isGen = generatingLessonId === lessonKey;
                                                                            return (
                                                                                <SOWTooltip text={isGen ? "Generating Lesson Plan..." : "Lesson Plan"}>
                                                                                    <button
                                                                                        disabled={isGen}
                                                                                        onClick={() => handleGenerateLessonPlanFromSOW(lesson, weekNum)}
                                                                                        className={`p-2 rounded-xl transition-all shadow-sm group ${
                                                                                            isGen 
                                                                                                ? 'bg-emerald-100 text-emerald-700 cursor-wait animate-pulse' 
                                                                                                : 'text-[#18216D] hover:text-white hover:bg-[#18216D] bg-indigo-50'
                                                                                        }`}
                                                                                    >
                                                                                        {isGen ? (
                                                                                            <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                                                                        ) : (
                                                                                            <BookOpenIcon className="w-5 h-5 group-hover:text-[#FFC425] transition-colors" />
                                                                                        )}
                                                                                    </button>
                                                                                </SOWTooltip>
                                                                            );
                                                                        })()}

                                                                        {/* Add Lesson Icon */}
                                                                        <SOWTooltip text="Add Lesson">
                                                                            <button
                                                                                onClick={() => handleAddLessonToWeek(parseInt(weekNum), lesson.$$id)}
                                                                                className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                                                                            >
                                                                                <PlusIcon className="w-5 h-5" />
                                                                            </button>
                                                                        </SOWTooltip>

                                                                        {/* Row Edit/Save Toggle */}
                                                                        {isLocalEditing ? (
                                                                            <div className="flex flex-col items-center gap-2">
                                                                                <SOWTooltip text="Save">
                                                                                    <button
                                                                                        onClick={() => { setRowEditingId(null); handleSaveRoadmap(); }}
                                                                                        className="p-2 text-emerald-500 hover:bg-emerald-50 rounded-xl transition-all shadow-sm bg-white border border-emerald-100"
                                                                                    >
                                                                                        <CheckCircleIcon className="w-5 h-5" />
                                                                                    </button>
                                                                                </SOWTooltip>
                                                                                <SOWTooltip text="Cancel">
                                                                                    <button
                                                                                        onClick={() => setRowEditingId(null)}
                                                                                        className="p-2 text-rose-400 hover:bg-rose-50 rounded-xl transition-all shadow-sm bg-white border border-rose-100"
                                                                                    >
                                                                                        <XMarkIcon className="w-5 h-5" />
                                                                                    </button>
                                                                                </SOWTooltip>
                                                                            </div>
                                                                        ) : (
                                                                            <SOWTooltip text="Edit Row">
                                                                                <button
                                                                                    onClick={() => setRowEditingId(lesson.$$id)}
                                                                                    disabled={isEditingRoadmap}
                                                                                    className={`p-2 rounded-xl transition-all ${isEditingRoadmap ? 'text-slate-200 cursor-not-allowed' : 'text-amber-400 hover:text-amber-600 hover:bg-amber-50'}`}
                                                                                >
                                                                                    <PencilIcon className="w-5 h-5" />
                                                                                </button>
                                                                            </SOWTooltip>
                                                                        )}

                                                                        {/* Delete Icon */}
                                                                        <SOWTooltip text="Delete Row" position="bottom">
                                                                            <button
                                                                                onClick={() => handleDeleteLesson(lesson.$$id)}
                                                                                className="p-2 text-rose-300 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                                                                            >
                                                                                <TrashIcon className="w-5 h-5" />
                                                                            </button>
                                                                        </SOWTooltip>
                                                                    </div>
                                                                </td>
                                                            )}
                                                        </tr>

                                                        {/* Full-width editor panel injected below the active row */}
                                                        {isLocalEditing && (
                                                            <RowEditorPanel
                                                                lesson={lesson}
                                                                lessonIndex={lessonIndex}
                                                                weekNum={weekNum}
                                                                strandOptions={strandOptions}
                                                                subStrandOptions={subStrandOptions}
                                                                outcomeOptions={outcomeOptions}
                                                                experienceOptions={experienceOptions}
                                                                registrySubStrand={registrySubStrand}
                                                                curriculumTree={curriculumTree}
                                                                handleUpdateLesson={handleUpdateLesson}
                                                                setRowEditingId={setRowEditingId}
                                                                handleSaveRoadmap={handleSaveRoadmap}
                                                                setEditorModal={setEditorModal}
                                                                teacherMode={teacherMode}
                                                            />
                                                        )}
                                                        </React.Fragment>
                                                );
                                            })}
                                            {!isHOS && !isViewOnly && (
                                                <tr className="bg-indigo-50/40 hover:bg-indigo-100/50 transition-all duration-300 border-b border-indigo-100/60 group/row">
                                                    <td colSpan={7} className="p-0">
                                                        <button
                                                            onClick={() => handleAddWeekBelow(parseInt(weekNum))}
                                                            className="w-full py-3.5 flex justify-center items-center gap-2 text-[10px] font-black text-indigo-400 uppercase tracking-[0.15em] hover:text-[#18216D] hover:bg-white/40 transition-all group"
                                                        >
                                                            <PlusIcon className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                                                            Add Week
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                    </tbody>
                                </table>
                            ) : renderAccordionView()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };



    return (
        <div className="space-y-8 min-h-screen">
            {isAutoView && selectedScheme ? (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {renderRoadmap(selectedScheme, readOnly || isHOS)}
                </div>
            ) : (
                <div className="p-12 text-center text-slate-500 bg-white rounded-[2.5rem] shadow-sm border border-slate-100">
                    <BookOpenIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No scheme of work selected.</p>
                </div>
            )}
            {/* Lesson Deletion Confirmation Modal */}
            <Modal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                title="Delete Lesson Row"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDeleteLesson}
                            disabled={submitting}
                            className="w-full sm:w-auto px-8 py-4 bg-rose-600 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-rose-700 transition-all shadow-lg shadow-rose-900/20 active:scale-95 disabled:opacity-50"
                        >
                            {submitting ? 'Deleting...' : 'Confirm Delete'}
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirmation(false)}
                            className="w-full sm:w-auto px-8 py-4 bg-slate-50 text-slate-400 rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-100 transition-all active:scale-95"
                        >
                            Back
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-[#18216D] font-bold leading-relaxed">
                        Are you sure you want to delete this lesson row? This will remove the sub-strand, specific outcomes, and all planning for this specific lesson.
                    </p>
                    <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3">
                        <i className="fas fa-exclamation-triangle text-amber-500 mt-1"></i>
                        <p className="text-[11px] font-black text-amber-800 uppercase tracking-wider leading-normal">
                            Once confirmed, the change will be persisted to the database.
                        </p>
                    </div>
                </div>
            </Modal>

            {/* Custom Advanced Editor Modal */}
            <ContentEditorModal
                isOpen={editorModal.isOpen}
                onClose={() => setEditorModal(prev => ({ ...prev, isOpen: false }))}
                title={editorModal.title}
                initialValue={editorModal.content}
                onSave={async (newVal) => {
                    // 1. Update local state
                    const updatedEdits = roadmapEdits.map(l =>
                        l.$$id === editorModal.lessonId ? { ...l, [editorModal.field]: newVal } : l
                    );
                    setRoadmapEdits(updatedEdits);

                    // 2. Persist to backend immediately
                    await handleSaveRoadmap(updatedEdits);

                    // 3. Close and notify
                    setEditorModal(prev => ({ ...prev, isOpen: false }));
                }}
            />

            {/* AI-Powered SOW Document Import Modal Helper */}
            {renderImportModal()}
        </div>
    );
};

export default SchemeOfWorkManagement;

